import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import SVG from 'components/renderSvg';
import { SidebarRoutes } from 'routes/SidebarRoutes';
import rightarrow from 'media/svgs/sidebar/rightarrow.svg';
import settingSlider from 'media/svgs/sidebar/settings-sliders.svg';
import { togglesidebar } from 'store/slices/commonSlice';
import useWindowSize from 'hooks/useWindowSize';

const SubMenu = ({ item, expand, submenuHeight, setSubmenuHeight }) => {
  const ref = useRef(null);
  const location = useLocation();
  const isExpand = expand.isExpand && expand.linkName === item.name;

  useEffect(() => {
    if (isExpand) {
      setSubmenuHeight(ref?.current?.scrollHeight);
    }
  }, [isExpand]);

  if (item?.subMenu?.length) {
    return (
      <ul
        ref={ref}
        className={`overflow-hidden pl-9 transition-[height] duration-300 ease-in-out`}
        style={{
          height: isExpand ? `${submenuHeight + 10}px` : '0',
        }}
      >
        {item.subMenu.map(child => {
          const isActive = location.pathname === child.path;
          return (
            <li
              key={child.name}
              className={`mt-1 rounded-lg transition-colors duration-200 ${isActive ? 'bg-brand-100/80 text-brand-800 font-semibold' : 'text-gray-900 hover:bg-gray-100'
                }`}
            >
              <Link
                to={child.path}
                className={`block w-full py-2 px-3 text-sm font-medium ${isActive ? 'text-brand-800' : 'text-gray-800'
                  }`}
              >
                {child.name}
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }
  return null;
};

const MenuItem = ({ item, isActive, hasSubMenu, badge, isToggle, isUnread }) => {
  return (
    <div className={`flex w-full items-center justify-between`}>
      <div className={`flex items-center gap-3 transition-colors duration-200`}>
        <div className="relative flex items-center justify-center">
          <span>
            {isActive && item.activeIcon ? item.activeIcon : item.icon}
          </span>
        </div>
        {!isToggle && (
          <span className={`text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis ${isActive ? 'text-white font-semibold' : 'text-gray-700'}`}>
            {item.name}
          </span>
        )}
      </div>

      {!isToggle && badge > 0 && (
        <span className="flex items-center justify-center min-w-[28px] h-[22px] px-1.5 text-[11px] font-semibold text-indigo-600 bg-white border border-indigo-200 rounded-full">
          +{badge > 99 ? '99' : badge}
        </span>
      )}

      {hasSubMenu && !isToggle && (
        <div className={`transform transition-transform duration-200 ${isActive ? 'rotate-90' : ''}`}>
          <SVG icon={rightarrow} />
        </div>
      )}
    </div>
  );
};


const Sidebar = ({ handleLogout }) => {
  const [expand, setExpand] = useState({
    isExpand: false,
    linkName: '',
  });

  const [totalTrialDays, setTotalTrialDays] = useState(7);
  const [daysLeft, setDaysLeft] = useState(0);
  const [submenuHeight, setSubmenuHeight] = useState(0);

  const location = useLocation();
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const { isToggle } = useSelector(state => state.commonState);
  const venderData = useSelector(state => state?.vender?.venderData);
  const { unreadCount } = useSelector(state => state.messaging);


  let profileStatus = 0;
  if (venderData?.addBatch) profileStatus += 20;
  if (venderData?.addHall) profileStatus += 20;
  if (venderData?.addTrainer) profileStatus += 20;
  if (venderData?.profileCompleted) profileStatus += 20;
  if (venderData?.serviceCompleted) profileStatus += 20;
  if (profileStatus > 100) profileStatus = 100;

  const handleExpand = (e, item) => {
    e.stopPropagation();
    if (item.subMenu?.length) {
      if (expand.linkName === item.name) {
        setExpand({ ...expand, isExpand: false, linkName: '' });
      } else {
        setExpand({ ...expand, isExpand: true, linkName: item.name });
      }
    }
  };

  useEffect(() => {
    SidebarRoutes?.forEach(item => {
      if (item?.subMenu) {
        item?.subMenu?.forEach(child => {
          if (child.path === location.pathname) {
            setExpand({ isExpand: true, linkName: item.name });
          }
        });
      }
    });
  }, [location.pathname]);

  const handleClick = () => {
    setExpand({ ...expand, isExpand: false, linkName: '' });
    // Close sidebar on mobile only when navigating
    if (width < 768 && !isToggle) {
      dispatch(togglesidebar());
    }
  };

  useEffect(() => {
    if (width < 1000 && !isToggle) {
      dispatch(togglesidebar());
    }
    if (width > 1000 && isToggle) {
      dispatch(togglesidebar());
    }
  }, [width]);

  useEffect(() => {
    try {
      const status = venderData?.subscription_tier || venderData?.status || 'free';
      if (status === 'pro' || status === 'active') {
        setDaysLeft(0);
        return;
      }

      const trialEndsAtString = venderData?.trial_ends_at || venderData?.trial_end || venderData?.trialEndsAtMillis;
      if (!trialEndsAtString) {
        setDaysLeft(0);
        return;
      }

      const trialEndsAt = new Date(trialEndsAtString);
      if (isNaN(trialEndsAt.getTime()) || (typeof trialEndsAtString === 'string' && trialEndsAtString.startsWith('0001'))) {
        setDaysLeft(0);
        return;
      }
      const now = new Date();
      const msLeft = Math.max(trialEndsAt.getTime() - now.getTime(), 0);
      const days = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
      setDaysLeft(days);

      // Dynamically calculate total trial days if possible
      const createdAtString = venderData?.created_at || venderData?.createdAt;
      if (createdAtString) {
        const createdAt = new Date(createdAtString);
        if (!isNaN(createdAt.getTime())) {
          const totalMs = trialEndsAt.getTime() - createdAt.getTime();
          const total = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
          if (total > 0) {
            setTotalTrialDays(total);
          }
        }
      }
    } catch (e) {
      setDaysLeft(0);
    }
  }, [venderData?.trial_ends_at, venderData?.trial_end, venderData?.trialEndsAtMillis, venderData?.subscription_tier, venderData?.status, venderData?.created_at, venderData?.createdAt]);

  const progressPercentage = ((totalTrialDays - daysLeft) / totalTrialDays) * 100;

  return (
    <>
      {/* Backdrop overlay - only on mobile when sidebar is open */}
      {!isToggle && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-30 backdrop-blur-sm"
          onClick={() => dispatch(togglesidebar())}
        />
      )}

      <div
        className={`fixed top-[0px] left-0 h-full z-40 bg-white border-r border-gray-100 shadow-soft transition-all duration-300 ease-in-out flex flex-col pb-2 pt-[64px]
        ${isToggle ? 'w-[80px] hidden md:flex' : 'w-[280px]'}`}
      >
        <div className="flex-1 overflow-y-auto px-4 scrollbar-thin scrollbar-thumb-gray-200">
          <ul className="space-y-1">
            {SidebarRoutes?.map(item => {
              const hasSubMenu = !!item?.subMenu;
              const isParentActive = expand.linkName === item.name || location.pathname === item.path;
              const isActive = location.pathname === item.path;
              const unreadBadge = item.name === 'Messages' ? unreadCount : 0;
              const isUnread = unreadBadge > 0 && !isParentActive;

              return (
                <li key={item.name} id={`tour-${item.name.toLowerCase()}`}>
                  {hasSubMenu ? (
                    <button
                      onClick={e => handleExpand(e, item)}
                      className={`group w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 outline-none
                      ${isParentActive
                          ? 'bg-black text-white'
                          : 'hover:bg-gray-50'}`}
                    >
                      <MenuItem
                        item={item}
                        isActive={isParentActive}
                        hasSubMenu={hasSubMenu}
                        badge={unreadBadge}
                        isToggle={isToggle}
                        isUnread={isUnread}
                      />
                    </button>
                  ) : (
                    <Link
                      to={item?.path}
                      onClick={handleClick}
                      className={`group w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 outline-none
                      ${location.pathname === item.path
                          ? 'bg-black text-white'
                          : 'hover:bg-gray-50'}`}
                    >
                      <MenuItem
                        item={item}
                        isActive={isActive}
                        hasSubMenu={false}
                        badge={unreadBadge}
                        isToggle={isToggle}
                        isUnread={isUnread}
                      />

                    </Link>
                  )}

                  <SubMenu
                    item={item}
                    expand={expand}
                    submenuHeight={submenuHeight}
                    setSubmenuHeight={setSubmenuHeight}
                  />
                </li>
              );
            })}

            <li className="mt-6 pt-4 border-t border-gray-100" id="tour-setup">
              <Link
                to={'setup'}
                onClick={handleClick}
                className={`group w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
                ${location.pathname === '/setup'
                    ? 'bg-brand-100 shadow-md border border-brand-200/50'
                    : 'hover:bg-gray-100'
                  }`}
              >
                <div className='flex items-center gap-3'>
                  <span className={`${location.pathname === '/setup' ? 'text-brand-600' : 'text-gray-500 group-hover:text-brand-600'}`}>
                    <SVG icon={settingSlider} />
                  </span>
                  {!isToggle && (
                    <span className={`text-[15px] font-medium ${location.pathname === '/setup' ? 'text-brand-600' : 'text-gray-800'}`}>
                      Setup
                    </span>
                  )}
                </div>
                {!isToggle && (
                  <div className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${location.pathname === '/setup' ? 'border-brand-300 text-brand-700 bg-white/50' : 'border-gray-200 text-gray-500'}`}>
                    {profileStatus}%
                  </div>
                )}
              </Link>
            </li>

            <li className="pt-4 border-t border-gray-100 mt-2">
              <a
                href="https://sportona.userjot.com/roadmap"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-gray-100"
              >
                <span className="text-gray-500 group-hover:text-brand-600 transition-colors flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </span>
                {!isToggle && (
                  <span className="text-[15px] font-medium text-gray-800 group-hover:text-brand-600 transition-colors">
                    Feedback & Roadmap
                  </span>
                )}
              </a>
            </li>
          </ul>
        </div>

        {/* Trial Status Footer */}
        {!isToggle && venderData?.subscription_tier !== 'pro' && (
          <div className="px-5 pb-6 pt-2">
            <div className="group block w-full p-4 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300">
              <div className="flex justify-between items-center mb-3">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Trial Period</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${daysLeft > 3 ? 'bg-gray-50 text-gray-600 border border-gray-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                  {daysLeft > 0 ? `${daysLeft}d left` : 'Expired'}
                </span>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-[9px] items-center mb-1.5 text-gray-400 font-bold uppercase tracking-wider">
                  <span>Usage</span>
                  <span>{Math.round(progressPercentage)}%</span>
                </div>
                <div className="w-full bg-gray-50 rounded-full h-1 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out 
                    ${daysLeft > 3 ? 'bg-black' : 'bg-red-500'}
                  `}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              <Link
                to={'/plans'}
                className="block w-full py-2 text-center rounded-lg bg-gray-50 hover:bg-black hover:text-white text-gray-900 text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-200 border border-gray-100 hover:border-black"
              >
                Upgrade Plan
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
